import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  Select,
  Skeleton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  MenuItem,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import CachedIcon from "@mui/icons-material/Cached";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import { CollapseAlert, LoadingButton, SnackbarAlert } from "../../../components/pages/common/styled";
import useAdException from "../../../hooks/super/useAdException";
import useOrganisation from "../../../hooks/super/useOrganisation";
import useAuth from "../../../hooks/useAuth";
import { formatDate } from "../../../utils/format";
import { UserType } from "../../../utils/constants";
import TablePagination from "../../../components/common/TablePagination";

import DeleteAdExceptionModal from "./component/M_DeleteAdException";
import AdExceptionModal from "./component/M_AdException";

import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import {
  TOKENS,
  StatTile,
  Monogram,
  RowMenu,
  EmptyState,
} from "../../../components/pages/_new/super/design";

const thCellSx = {
  padding: "10px 14px",
  borderBottom: `1px solid ${TOKENS.border}`,
  background: TOKENS.bgSunken,
  color: TOKENS.fg2,
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const tdCellSx = {
  padding: "12px 14px",
  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
  color: TOKENS.fg1,
  fontSize: 13.5,
};

function SAAdException() {
  const {
    ad_exceptions,
    organisation,
    total_ad_exception_count,
    from,
    rows_per_page,
    errMsg,
    getAdExceptions,
    applyAdCfg,
    setErr,
  } = useAdException();
  const { adminUserType } = useAuth();
  const { organisations, getOrganisations } = useOrganisation();

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  useEffect(() => getOrganisations(), [getOrganisations]);

  useEffect(() => {
    if (!organisations || organisations.length === 0) return;
    if (!organisation) getAdExceptions(organisations[0].id, rows_per_page, from);
  }, [organisation, organisations, getAdExceptions, rows_per_page, from]);

  const [page, setPage] = useState(0);
  const handlePageChange = async (e, newPage) => {
    await getAdExceptions(organisation, rows_per_page, newPage * rows_per_page);
    setPage(newPage);
  };
  const handleRowsPerPageChange = async (e) => {
    await getAdExceptions(organisation, e.target.value, 0);
    setPage(0);
  };

  const [curException, setCurException] = useState(null);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const deleteHandleClose = () => setDeleteOpen(false);
  const [updateOpen, setUpdateOpen] = React.useState(false);
  const updateHandleClose = () => setUpdateOpen(false);

  const [snackOpen, setSnackOpen] = React.useState(false);
  const handleSnackClose = () => setSnackOpen(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const [loading, setLoading] = React.useState(false);

  const apply = async () => {
    setLoading(true);
    const result = await applyAdCfg();
    setMessage(result.msg);
    setSuccess(result.status);
    setLoading(false);
    setSnackOpen(true);
  };

  const refresh = () => getAdExceptions(organisation, rows_per_page, from);

  const canEdit = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);
  const isLoading = ad_exceptions === null;
  const list = Array.isArray(ad_exceptions) ? ad_exceptions : [];

  return (
    <React.Fragment>
      <Helmet title="SA AD Exception" />
      <PageHeader
        title="Anti DDoS Exceptions"
        description="IP allow-lists per tenant — bypasses DDoS mitigation"
        actions={
          <>
            <OutlinedButton
              onClick={refresh}
              startIcon={<CachedIcon style={{ width: 18, height: 18 }} />}
            >
              Refresh
            </OutlinedButton>
            <LoadingButton
              variant="contained"
              startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
              loadingPosition="start"
              loading={loading}
              onClick={apply}
              disabled={isLoading || !canEdit}
              sx={{
                height: 36,
                borderRadius: "8px",
                backgroundColor: "#6371F7",
                fontSize: 14,
                fontWeight: 400,
                textTransform: "none",
                "&:hover": { backgroundColor: "#4959ee" },
              }}
            >
              Apply
            </LoadingButton>
          </>
        }
      />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatTile
          icon={<ShieldOutlinedIcon />}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : total_ad_exception_count || 0}
          label="Total exceptions"
        />
        <StatTile
          icon={<ShieldOutlinedIcon />}
          iconBg={TOKENS.successBg}
          iconInk={TOKENS.successInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : list.length}
          label="Visible on this page"
        />
        <StatTile
          icon={<ShieldOutlinedIcon />}
          iconBg={TOKENS.warningBg}
          iconInk={TOKENS.warningInk}
          value={organisations?.length || 0}
          label="Organisations"
        />
      </Box>

      <Box
        sx={{
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusM}px`,
          background: TOKENS.bgSurface,
          boxShadow: TOKENS.shadowXs,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: "14px 16px",
            borderBottom: `1px solid ${TOKENS.borderSubtle}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Select
            value={organisation || ""}
            onChange={(e) => getAdExceptions(e.target.value, rows_per_page, from)}
            size="small"
            sx={{
              minWidth: 280,
              "& .MuiOutlinedInput-input": { fontSize: 13.5 },
              borderRadius: "10px",
              background: TOKENS.bgSurface,
            }}
          >
            {organisations?.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.title}
              </MenuItem>
            ))}
          </Select>
          <Box sx={{ flex: 1 }} />
          <PrimaryButton
            onClick={() => {
              setCurException(null);
              setUpdateOpen(true);
            }}
            startIcon={<AddCircleOutlineIcon style={{ width: 18, height: 18 }} />}
            disabled={isLoading || !canEdit}
          >
            Add Exception
          </PrimaryButton>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thCellSx}>Domain</TableCell>
                <TableCell sx={thCellSx}>IP address</TableCell>
                <TableCell sx={thCellSx}>Created</TableCell>
                <TableCell sx={thCellSx} align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {[0, 1, 2, 3].map((j) => (
                      <TableCell key={j} sx={tdCellSx}>
                        <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ borderBottom: "none" }}>
                    <EmptyState
                      icon={<HighlightOffIcon />}
                      title="No registered exceptions"
                      body="Use the Add Exception button to allow specific IPs for this tenant."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                list.map((exception) => {
                  const actions = canEdit
                    ? [
                        {
                          key: "edit",
                          icon: <EditIcon fontSize="small" />,
                          label: "Edit",
                          onClick: () => {
                            setCurException(exception);
                            setUpdateOpen(true);
                          },
                        },
                        { key: "div", divider: true },
                        {
                          key: "delete",
                          icon: <DeleteIcon fontSize="small" />,
                          label: "Delete",
                          danger: true,
                          onClick: () => {
                            setCurException(exception);
                            setDeleteOpen(true);
                          },
                        },
                      ]
                    : [];
                  return (
                    <TableRow key={exception?._id} hover sx={{ "&:hover": { background: TOKENS.bgSunken } }}>
                      <TableCell sx={tdCellSx}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Monogram name={exception?.domain || "?"} size={32} />
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TOKENS.fg1 }}>
                            {exception?.domain || "—"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ ...tdCellSx, fontFamily: "ui-monospace, monospace", fontSize: 12.5 }}>
                        {exception?.ip_list || "—"}
                      </TableCell>
                      <TableCell sx={{ ...tdCellSx, color: TOKENS.fg2 }}>
                        {exception?.created_at ? formatDate(exception.created_at) : "—"}
                      </TableCell>
                      <TableCell sx={tdCellSx} align="right">
                        <RowMenu actions={actions} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total_ad_exception_count}
          rowsPerPage={rows_per_page}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>

      <DeleteAdExceptionModal open={deleteOpen} handleClose={deleteHandleClose} id={curException?._id} />
      <AdExceptionModal
        open={updateOpen}
        handleClose={updateHandleClose}
        organisation={organisation}
        ad_exception={curException}
      />
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default SAAdException;
